import React from "react";
import { Balancer } from "react-wrap-balancer";
import Image from "next/image";
import { type FlexProps, SimpleGrid, Box } from "@chakra-ui/react";
import { type Integration } from "lib/sanity";
import { Section, sectionSpacingY } from "components/section";
import { TextTitle } from "ui/text";
import SQLImage from "../../../../public/assets/integrations/connection/sql-query.svg";
import DbtImage from "../../../../public/assets/integrations/connection/dbt-query.png";
import LookerImage from "../../../../public/assets/integrations/connection/looker-query.png";
import TableImage from "../../../../public/assets/integrations/connection/table-query.png";
import SigmaImage from "../../../../public/assets/integrations/connection/sigma-query.png";
import BrowserImage from "../../../../public/assets/integrations/connection/custom-query.png";
import AudiencesImage from "../../../../public/assets/integrations/connection/visual-query.svg";
import { Card } from "./card";

enum QueryType {
  RawSql = "raw_sql",
  Table = "table",
  DbtModel = "dbt_model",
  LookerLook = "looker_look",
  Custom = "custom",
  Visual = "visual",
  Sigma = "sigma",
}

function getQueryTypeOptions(source: Integration) {
  const sourceQueryTypes = source.supportedQueryTypes
    ?.sort((a, b) => a.localeCompare(b))
    ?.filter((type) => Object.values(QueryType).includes(type as QueryType));

  return sourceQueryTypes?.map((type) => {
    switch (type) {
      case QueryType.RawSql: {
        return {
          label: "SQL editor",
          description: `Create and Edit SQL from your browser. Hightouch supports SQL native to ${source.name}.`,
          image: {
            src: SQLImage,
            alt: "SQL editor.",
          },
        };
      }

      case QueryType.Table: {
        return {
          label: "Table selector",
          description: `Select available tables and sheets from ${source.name} and sync using existing views without having to write SQL.`,
          image: {
            src: TableImage,
            alt: "Semi-opaque open dropdown with three example table names such as 'schema.table.name.1'.",
          },
        };
      }

      case QueryType.DbtModel: {
        return {
          label: "dbt model selector",
          description: "Sync directly with your dbt models saved in a git repository.",
          image: {
            src: DbtImage,
            alt: "Semi-opaque, open dropdown with three example dbt model names such as 'dbt.model.name.1'.",
          },
        };
      }

      case QueryType.LookerLook: {
        return {
          label: "Looker",
          description: "Query using Looks. Hightouch turns your look into SQL and will pull from your source.",
          image: {
            src: LookerImage,
            alt: "Semi-opaque open dropdown with three example Look names such as 'Look 1'.",
          },
        };
      }

      case QueryType.Sigma: {
        return {
          label: "Sigma model",
          description: `Hightouch converts your Sigma workbook element into a SQL query that runs directly on ${source.name}.`,
          image: {
            src: SigmaImage,
            alt: "Semi-opaque open dropdown with three example workbook names such as 'Workbook 1'.",
          },
        };
      }

      case QueryType.Custom: {
        return {
          label: "Custom",
          description: "Browse available data and query using a file, spreadsheet, collection, or other dataset.",
          image: {
            src: BrowserImage,
            alt: "Two semi-opaque radio buttons 'Source 1' (selected) and 'Source 2', and a semi-opaque closed dropdown reading 'Select and object'.",
          },
        };
      }

      case QueryType.Visual: {
        return {
          label: "Customer Studio",
          description: `For less technical users, pass traits and audiences from ${source.name} using our visual segmentation builder.`,
          image: {
            src: AudiencesImage,
            alt: "Visual query builder reading 'All rows that...' with a button labeled 'select a property'.",
          },
        };
      }

      default: {
        return undefined;
      }
    }
  });
}

type QueryTypesProps = {
  source: Integration;
} & FlexProps;

export function QueryTypes({ source, ...props }: QueryTypesProps) {
  const queryTypeOptions = getQueryTypeOptions(source);

  return (
    <Section my="0" py={sectionSpacingY} {...props}>
      <TextTitle
        size="lg"
        maxW="100%"
        title={<Balancer>What methods can I use to model my {source.name} data?</Balancer>}
      />

      <SimpleGrid as="ul" columns={{ base: 1, sm: 2, lg: 3 }} gap={{ base: 4, md: 7 }} mt={{ base: 10, md: 16 }}>
        {queryTypeOptions?.map((option) => {
          if (option) {
            return (
              <Card key={option.label} as="li">
                <Box w="100%" maxW={{ base: "300px", md: "100%" }} mt={1}>
                  <Image
                    src={option.image.src}
                    alt={option.image.alt}
                    width={300}
                    height={150}
                    style={{ objectFit: "contain", width: "100%", height: "auto" }}
                  />
                </Box>

                <TextTitle size="xs" titleAs="h3" title={option.label} description={option.description} />
              </Card>
            );
          }

          return null;
        })}
      </SimpleGrid>
    </Section>
  );
}
